import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import MaterialTable from "material-table";
import { makeStyles } from "@material-ui/core/styles";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteSharpIcon from "@material-ui/icons/DeleteSharp";
import EmptyCircleIcon from "@material-ui/icons/RadioButtonUnchecked";
import FilledCircleIcon from "@material-ui/icons/RadioButtonChecked";
import ArrowForwardIos from "@material-ui/icons/ArrowForwardIos";
import ArrowBackIos from "@material-ui/icons/ArrowBackIos";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import Tooltip from "@material-ui/core/Tooltip";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import clsx from "clsx";
import { ORDERS_TABLE_COLUMNS } from "constants/ui-constants";
import { getColumns, getOrdersActions, getLocalization } from "util/table-utils";
import { mapTableData, formatDeliveryNotes } from "util/helpers";
import { PATHS } from "util/appConstants";
import {
  selectOrders,
  selectOrderStatus,
  getOrders,
  deleteOrder,
  selectOrdersTimestamp,
} from "redux/slices/orderSlice";
import { addHandover, selectHandoverStatus } from "redux/slices/handoverSlice";
import { setShowMessage } from "redux/slices/uiSlice";
import { createRoute, selectRouteStatus } from "redux/slices/routeSlice";
import { selectUser } from "redux/slices/userSlice";
import {
  isDeliveryTillBeforeOrAtStartTime,
  resolveTourStartTimeForDeparture,
} from "util/deliveryTill";
import {
  getDeliveryTourStartId,
  getInboundHandoverStatus,
  getInboundHandoverVisualMeta,
  isHandoverOrder,
  isReturnOrder,
  shouldShowInboundHandoverIcon,
} from "util/handoverStatusUtils";
import withConfirm from "components/dialogs/delete";
import DarkLayout from "components/Shared/DarkLayout";
import Navbar from "components/Navbar";
import pen from "../../assets/img/pen.svg";
import { Select, Input, DatePicker, CustomMultiSelect } from "components/Shared/mui-formik-inputs";
import { formatCustomerLabel } from "util/orders/formatCustomerLabel";
import { getGroupingTour } from "util/tours/tourMergeHotfix";
import { sortOrder } from "util/customers/deliveryTimings";
import ImportOrderModal from "components/OrderModal/ImportOrderModal";
import ImportAtbasOrderModal from "components/OrderModal/ImportAtbasOrderModal";
import { getSupplierLocations, selectSupplierLocations } from "redux/slices/supplierLocationsSlice";
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { isOrderMissingDepositAgreement } from "components/Shared/OpeningHours/openingHoursUtils";
import HandshakeIcon from "@mui/icons-material/Handshake";
const locales = {
  "en-us": "en",
  "en-gb": "en-gb",
  en: "en",
  de: "de",
  "de-de": "de",
};

const hasAssignedRoute = (order) =>
  order?.route_id != null;

const useStyles = makeStyles({
  _1F1F1F: {
    background: "#1F1F1F",
  },
  _525252: {
    background: "#525252",
  },
  _edit: {
    background: "#6F9CEB ",
    borderRadius: "50%",
    padding: "2px",
    width: "13px",
    height: "13px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  _pointer: {
    cursor: "pointer",
  },
  input: {
    "& svg": {
      color: "white !important",
    },
    "& label": {
      color: "white !important",
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "white !important",
    },

    "& .MuiFilledInput-root": {
      color: "white !important",
      borderRadius: "4px !important",
      border: "1px solid white !important",
    },
  },
  button: {
    color: "white",
  },
  myCustomTooltip: {
    backgroundColor: "#93B5F0 !important",
    color: "#121212 !important",
    padding: "12px 16px !important",
    borderRadius: "4px !important",
    fontSize: "12px !important",
    fontFamily: "Roboto, sans-serif !important",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.3) !important",
  },
});

const tableTitle = "ORDERS";

const OrderList = ({ confirm }) => {
  const tableRef = useRef();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();
  const loading = useSelector(selectOrderStatus);
  const handoverLoading = useSelector(selectHandoverStatus);
  const routeLoading = useSelector(selectRouteStatus);
  const orders = useSelector(selectOrders);
  const timestamp = useSelector(selectOrdersTimestamp);
  const user = useSelector(selectUser);
  const [jsonData, setjsonData] = useState(orders);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [atbasImportModalOpen, setAtbasImportModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    departure_date: moment(),
    departure: null,
    tour_number: "",
    locations: [],
  });
  const supplierLocations = useSelector(selectSupplierLocations);
  const [filteredJsonData, setFilteredJsonData] = useState([]);
  const getSelectedOrders = () => {
    return jsonData.reduce((acc, row) => acc.concat(row.orders.filter((o) => o.checked)), []);
  };

  const filteredLocationNames = React.useMemo(() => {
    return supplierLocations
      .filter(
        (location) =>
          user?.permissions?.suppliers?.admin ||
          (location?.supplier_id && location?.supplier_id === user?.supplier_id) ||
          (location?.supplier?.supplier_group_id && location?.supplier?.supplier_group_id === user?.supplier_group_id)
      )
      .map((location) => location.name);
  }, [supplierLocations, user]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  useEffect(() => {
    dispatch(getSupplierLocations());
  }, [dispatch]);
  useEffect(() => {
    if (user && supplierLocations.length && user?.permissions?.orders?.show_supplier) {
      const defaultLocation = supplierLocations.find(
        (loc) => loc?.supplier_id === user?.supplier_id && loc?.supplier_group_id === user?.supplier_group_id
      );
      if (defaultLocation) {
        setSelectedLocations([defaultLocation.name]);
        setFilters((prev) => ({ ...prev, locations: [defaultLocation.name] }));
      }
    }
  }, [supplierLocations, user]);
  const fetchOrders = async () => {
    await dispatch(getOrders());
  };

  useEffect(() => {
    if (
      !filters.departure_date &&
      !filters.departure &&
      !filters.tour_number &&
      (!filters.locations || filters.locations.length === 0)
    ) {
      return setFilteredJsonData(jsonData);
    }

    const filtered = jsonData.reduce((acc, row) => {
      let orders = row.orders;

      if (filters.departure_date) {
        orders = row.orders.filter((order) => {
          const departure_date = moment(`${order.departure_date} 12:00:00`, "YYYY-MM-DD HH:mm:ss").utc();

          if (moment().isSame(filters.departure_date, "day")) {
            return !order.departure_date || filters.departure_date.utc().isSameOrAfter(departure_date, "day");
          }

          return !order.departure_date || filters.departure_date.utc().isSame(departure_date, "day");
        });
      }

      if (filters.departure) {
        orders = orders.filter((order) => order.departure === filters.departure);
      }

      if (filters.tour_number) {
        orders = orders.filter((order) => {
          const tour = getGroupingTour(order);
          const searchStr = `${tour?.id || ""} ${tour?.name || ""} ${tour?.user_defined_tour_id || ""}`.toLowerCase();
          return searchStr.includes(filters.tour_number.toLowerCase().trim());
        });
      }

      if (filters.locations && filters.locations.length > 0) {
        const selectedLocationIds = filters.locations
          .map((name) => {
            const loc = supplierLocations.find((l) => l.name === name);
            return loc ? loc.id : null;
          })
          .filter((id) => id !== null);

        orders = orders.filter((order) => {
          return selectedLocationIds.includes(order.supplier_location_id);
        });
      }

      if (!orders.length) {
        return acc;
      }

      return acc.concat([
        {
          ...row,
          orders,
        },
      ]);
    }, []);

    setFilteredJsonData(filtered);
  }, [jsonData, filters]);

  useEffect(() => {
    if (!orders.length && !loading && !timestamp) {
      fetchOrders();
    }

    setjsonData(orders);
  }, [orders, loading]);

  useEffect(() => {
    if (timestamp) {
      fetchOrders();
    }
  }, []);

  const changeDepartureDate = (value) => {
    if (value === 1) {
      setFilters((f) => ({ departure_date: f.departure_date.add(1, "day") }));
    } else {
      setFilters((f) => ({
        departure_date: f.departure_date.subtract(1, "day"),
      }));
    }
  };
  const handleImportOrders = () => {
    setImportModalOpen(true);
  };
  const handleAtbasImportOrders = () => {
    setAtbasImportModalOpen(true);
  };

  const callbackOnDelete = (e, order) => {
    e.stopPropagation();
    confirm(() => dispatch(deleteOrder(order.id)), {
      description: "Are you sure?",
    });
  };

  const addHandler = () => {
    history.push(PATHS.orders.add);
  };
  const editHandler = (rowData) => {
    history.push(PATHS.orders.edit.replace(":id", rowData.id));
  };

  const createHandover = async () => {
    if (handoverLoading) {
      return;
    }

    const rows = getSelectedOrders();

    if (!rows.length) {
      return;
    }
    const userOwnedRows = rows.filter(
      (o) =>
        o?.supplier_id === user?.supplier_id &&
        o?.supplier_group_id === user?.supplier_group_id &&
        o?.supplier_location_id === user?.supplier_location_id
    );

    if (!userOwnedRows.length) {
      return;
    }

    await dispatch(addHandover({ order_ids: rows.map((o) => o.id) }, t));
    fetchOrders();
  };

  const checkChangeHandler = (e, clickedRow) => {
    e.stopPropagation();

    const newData = tableRef.current.state.data.map((row) => {
      if (row.id !== clickedRow.id) {
        return {
          ...row,
        };
      }

      return {
        ...row,
        mainCheck: e.target.checked,
        orders: row.orders.map((o) => ({
          ...o,
          checked: e.target.checked,
        })),
      };
    });

    setjsonData(newData);
  };

  const startTourCheck = () => {
    return !!jsonData.find((row) => {
      return row.orders.some((o) => o.checked);
    });
  };

  const startOrders = async () => {
    if (!startTourCheck() || routeLoading || handoverLoading) {
      return;
    }

    const rows = jsonData.filter((row) => {
      return row.orders.some((o) => o.checked);
    });

    if (!rows.length) {
      return;
    }

    const shouldUseDeliveryType =
      user?.permissions?.routesCreateDeliveryOrder || user?.account_permissions?.create_delivery;
    const routeType = shouldUseDeliveryType ? "DELIVERY" : null;
    console.log("routeType", routeType, user?.permissions?.routesCreateDeliveryOrder, user?.account_permissions?.create_delivery);

    confirm(
      async () => {
        try {
          for (const row of rows) {
            const selectedOrders = row.orders.filter((o) => o.checked);
            const tour = row.Tour;
            const invalidTillOrders = selectedOrders.filter((o) => {
              if (!o?.express || !o?.delivery_till) return false;
              const startTime =
                resolveTourStartTimeForDeparture(tour, o.departure) ||
                resolveTourStartTimeForDeparture(o?.Customer?.Tour, o.departure);
              return isDeliveryTillBeforeOrAtStartTime(o.delivery_till, startTime);
            });
            if (invalidTillOrders.length) {
              dispatch(
                setShowMessage({
                  description: t("DELIVERY_TILL_BEFORE_START_TIME"),
                  type: "error",
                })
              );
              return;
            }
          }

          let i = 0;
          let didCreateRoute = false;
          let hasTimeConstraintWarnings = false;
          const handoverOrdersToCreateAll = [];
          while (i < rows.length) {
            const selectedOrders = rows[i].orders.filter((o) => o.checked);
            const tour = rows[i].Tour;
            const isPaperBased = tour?.middle_mile_digital === false;
            const handoverOrdersMissingProtocol = selectedOrders.filter(
              (o) => isHandoverOrder(o, user) && o?.handover_id == null
            );

            const startableHandoverOrdersWithoutProtocol = isPaperBased
              ? handoverOrdersMissingProtocol.filter((o) => {
                const tourStartId = getDeliveryTourStartId(o);
                if (tourStartId == null || user?.supplier_location_id == null) return false;
                return String(tourStartId) === String(user.supplier_location_id);
              })
              : [];

            const handoverOrdersToCreate = isPaperBased
              ? handoverOrdersMissingProtocol.filter(
                (o) => !startableHandoverOrdersWithoutProtocol.includes(o)
              )
              : handoverOrdersMissingProtocol;

            const regularOrders = selectedOrders
              .filter((o) => !isHandoverOrder(o, user) || o?.handover_id != null)
              .concat(startableHandoverOrdersWithoutProtocol);

            if (regularOrders.length) {
              const created = await dispatch(
                createRoute(
                  {
                    order_ids: regularOrders.map((o) => o.id),
                    tour_id: tour.id,
                    type: routeType,
                  },
                  t
                )
              );
              if (created?.hasTimeConstraintWarnings) {
                hasTimeConstraintWarnings = true;
              }
              didCreateRoute = true;
            }
            if (handoverOrdersToCreate.length) {
              handoverOrdersToCreateAll.push(...handoverOrdersToCreate);
            }

            i += 1;
          }

          if (handoverOrdersToCreateAll.length) {
            await dispatch(addHandover({ order_ids: handoverOrdersToCreateAll.map((o) => o.id) }, t));
          }

          if (didCreateRoute && !hasTimeConstraintWarnings) {
            dispatch(
              setShowMessage({
                description: "The routes were created successfully",
                type: "success",
              })
            );
          }

          fetchOrders();
        } catch (err) {
          console.error(err);
        }
      },
      {
        description: `${t("Start route with the selected orders?")}`,
      }
    );
  };

  const actions = getOrdersActions({
    callbackOnDelete: (e, rowData) => callbackOnDelete(e, rowData),
    addHandler: () => addHandler(),
    editHandler: (e, rowData) => editHandler(rowData),
    startTourCheck: () => startTourCheck(),
    startTour: () => startOrders(),
    createHandover: () => createHandover(),
    t,
    user,
    importAction: () => handleImportOrders(),
    atbasImportAction: () => handleAtbasImportOrders(),
    routeLoading,
    handoverLoading,
  });

  const innerChangeHandler = (order) => {
    const orderTourId = getGroupingTour(order)?.id;
    const newData = tableRef.current.state.data.map((row) => {
      if (row.Tour.id !== orderTourId) {
        return {
          ...row,
        };
      }

      return {
        ...row,
        orders: row.orders.map((o) => ({
          ...o,
          checked: o.id === order.id ? !o.checked : o.checked,
        })),
      };
    });

    setjsonData(newData);
  };

  return (
    <>
      <Navbar />
      <DarkLayout loading={loading || handoverLoading}>
        <MuiPickersUtilsProvider
          utils={MomentUtils}
          locale={locales[i18n.language.toLowerCase()]}
          libInstance={moment}
        >
          <Box className={classes.input} display="flex" alignItems="center">
            <Box mr={2}>
              <DatePicker
                onChange={(date) => {
                  if (date && date.isValid()) {
                    setFilters((f) => ({ ...f, departure_date: date }));
                  }

                  if (date === null) {
                    setFilters((f) => ({ ...f, departure_date: null }));
                  }
                }}
                name="departure_date"
                value={filters.departure_date}
                errors={{}}
                label="Departure date"
                clearable
                inputVariant="outlined"
              />
            </Box>
            <Box mr={0.5}>
              <Button
                size="small"
                className={classes.button}
                disabled={!filters.departure_date}
                onClick={() => changeDepartureDate(-1)}
              >
                <ArrowBackIos />
              </Button>
            </Box>
            <Box>
              <Button
                size="small"
                className={classes.button}
                disabled={!filters.departure_date}
                onClick={() => changeDepartureDate(1)}
              >
                <ArrowForwardIos />
              </Button>
            </Box>
            <Box ml={1} width="150px">
              <Select
                label={t("Departure")}
                name="departure"
                onChange={(e) => setFilters((f) => ({ ...f, departure: e.target.value }))}
                onBlur={() => { }}
                value={filters.departure}
                errors={{}}
                options={[{ value: null, label: t("None") }].concat(
                  [...new Set(orders?.flatMap((tourObj) => tourObj?.orders?.map((order) => order?.departure)))]
                    .sort((a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b))
                    .map((o) => ({
                      label: t(o),
                      value: o,
                    }))
                )}
              />
            </Box>
            <Box ml={2} width="150px">
              <Input
                label={t("Tour Number")}
                name="tour_number"
                onChange={(e) => {
                  setFilters((f) => ({ ...f, tour_number: e.target.value }));
                }}
                value={filters.tour_number}
              />
            </Box>
            {user?.permissions?.orders?.show_supplier && (
              <Box ml={2} width="150px" sx={{ marginTop: "-4px" }}>
                <CustomMultiSelect
                  values={filteredLocationNames}
                  setValues={selectedLocations}
                  onChange={(event) => {
                    const newSelectedValues = event.target.value;
                    setSelectedLocations(newSelectedValues);
                    setFilters((prev) => ({
                      ...prev,
                      locations: newSelectedValues,
                    }));
                  }}
                  label={t("Supplier Locations")}
                  labelId="supplier-locations-label"
                  required={false}
                  labelColor="white"
                />
              </Box>
            )}
          </Box>
        </MuiPickersUtilsProvider>
        <MaterialTable
          tableRef={tableRef}
          data={mapTableData(filteredJsonData)}
          title={t(tableTitle)}
          columns={getColumns(
            ORDERS_TABLE_COLUMNS((e, rowData) => checkChangeHandler(e, rowData), t, user),
            t
          )}
          actions={actions}
          localization={getLocalization(t)}
          options={{
            pageSize: 50,
            pageSizeOptions: [50, 100],
            detailPanelColumnAlignment: "right",
            paging: false,
            actionsColumnIndex: -1,
            search: false,
            headerStyle: {
              backgroundColor: "#121212",
              color: "white",
              borderBottom: "1px solid #525252",
              font: "normal normal normal 12px/24px Roboto",
              fontWeight: "bold",
            },
            cellStyle: {
              color: "white",
              border: "none",
              font: "normal normal normal 12px/24px Roboto",
            },
            showTitle: false,
            header: false,
            showTextRowsSelected: false,
            showSelectAllCheckbox: false,
            rowStyle: (rowData) => {
              if (rowData.tableData.id % 2 === 0) {
                return { backgroundColor: " #1F1F1F ", height: "71px" };
              } else {
                return { backgroundColor: "#525252", height: "71px" };
              }
            },
          }}
          detailPanel={[
            {
              icon: () => <ExpandMoreIcon />,
              openIcon: () => <ExpandLessIcon />,
              render: (rowData) => {
                return (
                  <>
                    {rowData.orders.map((order, i) => (
                      <Box className={clsx(rowData.tableData.id % 2 === 0 ? classes._1F1F1F : classes._525252)} key={i}>
                        <Box display="flex" py={2}>
                          <Box flex={2} pl={8}>
                            {formatCustomerLabel(order.Customer)}
                            {isOrderMissingDepositAgreement(order) && (
                              <Tooltip title={t("No deposit agreement available. Please add a deposit agreement.")}>
                                <WarningAmberIcon
                                  style={{ color: "#f0ad4e", fontSize: 18, marginLeft: 8, verticalAlign: "middle" }}
                                />
                              </Tooltip>
                            )}
                            <Box fontWeight="bold">
                              {t("Departure date")}:{" "}
                              {order.departure_date
                                ? moment(`${order.departure_date} 12:00:00`, "YYYY-MM-DD HH:mm:ss")
                                  .utc()
                                  .format("DD.MM.YYYY")
                                : "-"}
                            </Box>
                          </Box>
                          <Box flex={1} ml={5}>
                            {order.express ? "Express" : ""}
                          </Box>
                          <Box flex={1} ml={5}>
                            {
                              isReturnOrder(order)
                                ? t("Return")
                                : isHandoverOrder(order, user)
                                  ? t("Handover Order")
                                  : t(order.order_type[0]?.toUpperCase() + order.order_type.slice(1))
                            }
                          </Box >
                          <Box flex={1}>
                            {/* Display delivery notes */}
                            {(order.delivery_notes && Array.isArray(order.delivery_notes)) ||
                              (order.DeliveryNotes && Array.isArray(order.DeliveryNotes)) ? (
                              <div>
                                {(() => {
                                  const deliveryNotes = order.delivery_notes || order.DeliveryNotes;
                                  const { numbers, packages } = formatDeliveryNotes(deliveryNotes);
                                  return (
                                    <>
                                      <div
                                        style={{
                                          fontWeight: "bold",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "200px",
                                        }}
                                        title={numbers}
                                      >
                                        {numbers}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#ADADAD",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "200px",
                                        }}
                                        title={`${packages} ${t("Pckg")}`}
                                      >
                                        {packages} {t("Pckg")}
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              // Fallback for legacy orders
                              <div>
                                <div style={{ fontWeight: "bold" }}>{order.number}</div>
                                <div style={{ fontSize: "12px", color: "#ADADAD" }}>
                                  {order.packages} {t("Pckg")}
                                </div>
                              </div>
                            )}
                          </Box>
                          {
                            user?.permissions?.orders?.show_supplier && (
                              <Box flex={1}>
                                {(() => {
                                  const supplierLocation = supplierLocations.find(
                                    (loc) => loc.id === order.supplier_location_id
                                  );
                                  return supplierLocation ? supplierLocation.name : "";
                                })()}
                              </Box>
                            )
                          }

                          <Box
                            flex={2}
                            style={{
                              textAlign: "right",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                              {(() => {
                                if (shouldShowInboundHandoverIcon(order, user)) {
                                  const status = getInboundHandoverStatus(order, user);
                                  const meta = getInboundHandoverVisualMeta(status, t);
                                  if (!meta) {
                                    return null;
                                  }
                                  return (
                                    <Tooltip
                                      classes={{ tooltip: classes.myCustomTooltip }}
                                      title={
                                        <span>
                                          {t("Handover Order")}
                                          <br />
                                          {t("Status")}: {meta.label}
                                        </span>
                                      }
                                      placement="top"
                                      arrow
                                    >
                                      <HandshakeIcon style={{ color: meta.color, fontSize: "18px" }} />
                                    </Tooltip>
                                  );
                                }
                                if (isHandoverOrder(order, user)) {
                                  return (
                                    <Tooltip
                                      classes={{ tooltip: classes.myCustomTooltip }}
                                      title={t("Handover Order")}
                                      placement="top"
                                      arrow
                                    >
                                      <HandshakeIcon style={{ color: "#ADADAD", fontSize: "18px" }} />
                                    </Tooltip>
                                  );
                                }
                                return null;
                              })()}
                            </Box>
                            <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                              <div onClick={() => editHandler(order)} className={clsx(classes._edit, classes._pointer)}>
                                <img alt="icon" src={pen} style={{ height: "10px" }} />
                              </div>
                            </Box>
                            {(() => {
                              const canSelect =
                                user?.permissions?.orders?.allow_changes_supplier_group_level ||
                                (
                                  (user?.permissions?.orders?.show_supplier ||
                                    user?.permissions?.customers?.list ||
                                    (user?.permissions?.handovers?.create && user?.permissions?.orders?.show_supplier)) &&
                                  ((user?.permissions?.handovers?.create || user?.permissions?.routes?.show_route_button) &&
                                    order.supplier_id === user.supplier_id &&
                                    order.supplier_group_id === user.supplier_group_id &&
                                    order.supplier_location_id === user.supplier_location_id)
                                );

                              if (!canSelect) {
                                return <Box flex={1} pr={1.6} textAlign="center" />;
                              }

                              if (order?.Customer?.geo_location_status === 'error') {
                                return (
                                  <Box flex={1} pr={1.6} textAlign="center">
                                    <ErrorIcon style={{ color: 'red' }} titleAccess={t("Customer address is invalid. Please review and correct the address.")} />
                                  </Box>
                                );
                              }

                              if (!hasAssignedRoute(order)) {
                                return (
                                  <Box flex={1} pr={1.6} textAlign="center">
                                    <input
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        innerChangeHandler(order);
                                      }}
                                      className={"radio-checkbox"}
                                      id={`panel${order.id}`}
                                      type="checkbox"
                                      name="field"
                                      checked={!!order.checked}
                                    />
                                    <label className="radio-checkbox-label" htmlFor={`panel${order.id}`}>
                                      {order.checked ? <FilledCircleIcon /> : <EmptyCircleIcon />}
                                    </label>
                                  </Box>
                                );
                              }

                              return <Box flex={1} pr={1.6} textAlign="center" />;
                            })()}


                            <Box flex={1} display="flex" alignItems="center" justifyContent="flex-end">
                              <DeleteSharpIcon
                                className={classes._pointer}
                                style={{
                                  marginRight: "20px",
                                  color: "#ADADAD",
                                }}
                                onClick={(e) => {
                                  callbackOnDelete(e, order);
                                }}
                              />
                            </Box>
                          </Box>
                        </Box >
                      </Box >
                    ))}
                  </>
                );
              },
            },
          ]}
        />
      </DarkLayout >
      {
        user?.permissions?.orders?.import && (
          <ImportOrderModal
            open={importModalOpen}
            onClose={() => setImportModalOpen(false)}
            t={t}
            onImportComplete={() => fetchOrders()}
          />
        )
      }
      {
        user?.permissions?.orders?.atbas_scan_import && (
          <ImportAtbasOrderModal
            open={atbasImportModalOpen}
            onClose={() => setAtbasImportModalOpen(false)}
            t={t}
            onImportComplete={() => fetchOrders()}
          />
        )
      }
    </>
  );
};

export default withConfirm(OrderList);
